import {
  Badge,
  Button,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { Control, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import type {
  AudienceData,
  InterestsAudienceData,
} from "@common/interfaces/KYDMarketing";
import { isInterestsAudience } from "@common/utils/interestAudiences";
import trashIconSrc from "../assets/trash-icon.svg";
import { audienceTargetingKey } from "@common/utils/customAudienceTargetingKey";
import { SELECTED_AUDIENCES_STYLES } from "./selectedAudiencesPanelStyles";

const styles = SELECTED_AUDIENCES_STYLES;

function metaAudienceBadgeLabel(audience: AudienceData): string {
  if (isInterestsAudience(audience)) return "META • INTERESTS";
  switch (audience.audience_type) {
    case "meta":
    case "page_engagers":
    case "page_lookalike":
      return "META";
    case "kyd":
      return "KYD";
    case "csv":
      return "CSV";
    default:
      return "AUDIENCE";
  }
}

type SelectedAudiencesPanelProps = {
  audiences: AudienceData[];
  control: Control<FieldValues>;
  fieldName: string;
  onEditInterestsAudience?: (audience: InterestsAudienceData) => void;
};

function TrashIconButton({
  ariaLabel,
  onClick,
}: {
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <IconButton
      aria-label={ariaLabel}
      icon={
        <Image
          src={trashIconSrc}
          alt=""
          w={styles.trashIconW}
          h={styles.trashIconH}
          objectFit="contain"
        />
      }
      variant="outline"
      borderWidth="1px"
      borderColor="transparent"
      sx={{
        borderColor: "#e2e2e2",
        bg: styles.actionBg,
        borderRadius: styles.actionRadius,
        px: styles.actionPx,
        py: styles.actionPy,
        boxShadow: styles.actionShadowIcon,
        minW: "auto",
        h: "auto",
        fontSize: 0,
      }}
      _focusVisible={{
        boxShadow: `${styles.actionShadowIcon}, 0 0 0 2px #1d74ff`,
      }}
      onClick={onClick}
    />
  );
}

export default function SelectedAudiencesPanel({
  audiences,
  control,
  fieldName,
  onEditInterestsAudience,
}: SelectedAudiencesPanelProps) {
  const showEmpty = audiences.length === 0;

  return (
    <VStack
      align="stretch"
      spacing={`${styles.sectionGapPx}px`}
      w="full"
      fontFamily={styles.fontFamily}
    >
      <VStack align="flex-start" spacing={`${styles.labelStackGapPx}px`} w="full">
        <Text
          fontWeight={styles.titleFontWeight}
          fontSize={styles.titleFontSize}
          color={styles.titleColor}
          lineHeight={styles.titleLineHeight}
        >
          Selected audiences
        </Text>
        <Text
          fontSize={styles.subtitleFontSize}
          fontWeight={styles.subtitleFontWeight}
          color={styles.subtitleColor}
          lineHeight={styles.subtitleLineHeight}
        >
          Create a new audience or reuse one from past campaigns.
        </Text>
      </VStack>

      {showEmpty ? (
        <Text
          fontSize={styles.subtitleFontSize}
          fontWeight={styles.subtitleFontWeight}
          color={styles.subtitleColor}
          lineHeight={styles.subtitleLineHeight}
        >
          No audiences selected yet. Search below to add Meta audiences, or
          create one using the options above.
        </Text>
      ) : null}

      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <VStack
            align="stretch"
            spacing={`${styles.sectionGapPx}px`}
            w="full"
          >
            {audiences.map((audience, index) => {
              const rowKey =
                audienceTargetingKey(audience) ||
                audience.external_id ||
                audience.id ||
                `${audience.name ?? "audience"}-${index}`;
              const isInterests = isInterestsAudience(audience);
              return (
                <HStack
                  key={String(rowKey)}
                  align="center"
                  spacing={`${styles.cardGapPx}px`}
                  w="full"
                  p={styles.cardPadding}
                  bg={styles.cardBg}
                  border={styles.cardBorder}
                  borderRadius={styles.cardRadius}
                  boxShadow={styles.cardShadow}
                  overflow="hidden"
                >
                  <VStack
                    align="flex-start"
                    spacing={`${styles.labelStackGapPx}px`}
                    flex={1}
                    minW={0}
                  >
                    <Badge
                      bg={styles.tagBg}
                      color={styles.tagColor}
                      fontWeight={styles.tagFontWeight}
                      fontSize={styles.tagFontSize}
                      lineHeight={styles.tagLineHeight}
                      px={styles.tagPx}
                      py={styles.tagPy}
                      borderRadius={styles.tagRadius}
                      textTransform="none"
                      fontFamily={styles.fontFamily}
                    >
                      {metaAudienceBadgeLabel(audience)}
                    </Badge>
                    <Text
                      fontSize={styles.nameFontSize}
                      fontWeight={styles.nameFontWeight}
                      color={styles.nameColor}
                      lineHeight={styles.nameLineHeight}
                      noOfLines={2}
                      wordBreak="break-word"
                      fontFamily={styles.fontFamily}
                      title={audience.name}
                    >
                      {audience.name}
                    </Text>
                  </VStack>
                  {isInterests && onEditInterestsAudience ? (
                    <Button
                      variant="outline"
                      borderWidth="1px"
                      borderColor="transparent"
                      sx={{
                        borderColor: "#e2e2e2",
                        bg: styles.actionBg,
                        borderRadius: styles.actionRadius,
                        px: styles.actionPx,
                        py: styles.actionPy,
                        boxShadow: styles.actionShadowEdit,
                        fontFamily: styles.fontFamily,
                        fontSize: styles.editFontSize,
                        fontWeight: styles.editFontWeight,
                        color: styles.editTextColor,
                        lineHeight: styles.editLineHeight,
                        h: "auto",
                        minH: 0,
                      }}
                      _focusVisible={{
                        boxShadow: `${styles.actionShadowEdit}, 0 0 0 2px #1d74ff`,
                      }}
                      onClick={() =>
                        onEditInterestsAudience(audience as InterestsAudienceData)
                      }
                    >
                      Edit
                    </Button>
                  ) : null}
                  <TrashIconButton
                    ariaLabel={`Remove ${audience.name ?? "audience"}`}
                    onClick={() => {
                      const current = (field.value || []) as AudienceData[];
                      field.onChange(
                        current.filter((_, rowIndex) => rowIndex !== index)
                      );
                    }}
                  />
                </HStack>
              );
            })}
          </VStack>
        )}
      />
    </VStack>
  );
}
